/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import * as attributes from "./dndAttributes";
import { calculateBox, isElement, isHTMLElement } from "@adapptio/lib-shared";
import { IDndDraggableInfo, TDndDraggableID } from "./DndTypes";

export function findClosesDragHandleFromEvent(contextId: string, event: MouseEvent): HTMLElement {
	const target = event.target;

	if (!isElement(target)) {
		return null;
	}

	const selector = `[${attributes.dragHandle.contextId}="${contextId}"]`;
	const handle = target?.closest(selector);

	if (!isHTMLElement(handle)) {
		return null;
	}

	return handle;
}

export function findClosestDraggableId(contextId: string, event: MouseEvent): string {
	const handle = findClosesDragHandleFromEvent(contextId, event);

	if (!handle) {
		return null;
	}

	return handle.getAttribute(attributes.dragHandle.draggableId);
}

export function findClosestDropAreaIdFromEvent(contextId: string, event: MouseEvent): string {
	const target = event.target;

	if (!isElement(target)) {
		return null;
	}

	const selector = `[${attributes.droppable.contextId}="${contextId}"]`;
	const droppable = target?.closest(selector);

	if (!isHTMLElement(droppable)) {
		return null;
	}

	return droppable.getAttribute(attributes.droppable.droppableId);
}

export function preventHtml5Dnd(event: DragEvent): void {
	event.preventDefault();
}

export function getDraggableInfo({
	draggableId,
	el
}: {
	draggableId: TDndDraggableID;
	el: HTMLElement;
}): IDndDraggableInfo {
	const styles = window?.getComputedStyle(el);
	const borderBox: ClientRect = el.getBoundingClientRect();
	const box = calculateBox(borderBox, styles);

	const display = styles.display;

	const tagName = el.tagName.toLowerCase();

	return {
		draggableId,
		tagName,
		box,
		display
	};
}
